import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import useDiscardRun from "shared/Run/useDiscardRun";
import { AccessResult } from "utils/access";
import Tooltip from "ds/components/Tooltip";

import { useRunShortcut } from "../../hooks/useRunShortcut";

type DiscardRunButtonProps = {
  stackId: string;
  runId: string;
  runQueryToRefetch?: string;
  canDiscard: AccessResult;
};

const DiscardRunButton = ({
  runId,
  stackId,
  runQueryToRefetch,
  canDiscard,
}: DiscardRunButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const [discardRun, { loading, data }] = useDiscardRun({
    refetchQueries: [runQueryToRefetch || "GetRun"],
  });

  const onClick = useCallback(
    () =>
      discardRun({ stack: stackId, run: runId, note: null }, (data) => {
        if (data?.runDiscard?.id) {
          reportSuccess({ message: `Run ${data.runDiscard.id} successfully discarded` });
        }
      }),
    [discardRun, reportSuccess, runId, stackId]
  );

  const isLoading = loading || !!data?.runDiscard?.id;

  useRunShortcut({
    label: "Discard",
    code: "mod+alt+x",
    callback: onClick,
    loading: isLoading,
    disabled: !canDiscard.result,
  });

  if (!canDiscard.result && !canDiscard.message) {
    return null;
  }

  return (
    <Tooltip
      active={!!canDiscard.message}
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          size="small"
          disabled={isLoading || !canDiscard.result}
          loading={isLoading}
          onPress={onClick}
        >
          Discard
        </Button>
      )}
    >
      {canDiscard.message}
    </Tooltip>
  );
};

export default DiscardRunButton;
